/**
 * Where toasts are drawn — one region, mounted once by `App`.
 *
 * Two decisions worth their comment:
 *
 * - **The timer lives here, not in the store.** `push()` stays a pure state change, so a test
 * can raise a toast without owning a clock, and the auto-dismiss is a plain effect that
 * cleans itself up. A store that scheduled its own `setTimeout` would leak one per toast
 * raised while nothing was mounted.
 * - **`aria-live="polite"`, never `assertive`.** The narration region is the one live
 * region a player is actually reading; an assertive toast interrupts it mid-sentence to
 * announce that a story was deleted. Errors that a player must *act* on do not come through
 * here at all — they are rendered where the action is.
 */
import { useEffect } from 'react';
import { TOAST_TIMEOUT_MS, useToastStore, type Toast } from '@/ui/toast';

export function ToastHost() {
  const toasts = useToastStore((state) => state.toasts);

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-24 z-40 flex flex-col items-center gap-2 px-4"
    >
      {toasts.map((toast) => (
        <ToastRow key={toast.id} toast={toast} />
      ))}
    </div>
  );
}

/**
 * The dismiss goes through `getState()` rather than a prop or a subscribed action, so the
 * effect's only dependency is the toast's id. A callback prop would be a fresh closure on
 * every render of the host, and the timer would restart every time *any* toast was raised.
 */
function ToastRow({ toast }: { toast: Toast }) {
  const id = toast.id;

  useEffect(() => {
    const timer = setTimeout(() => {
      useToastStore.getState().dismiss(id);
    }, TOAST_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [id]);

  return (
    <button
      type="button"
      onClick={() => {
        useToastStore.getState().dismiss(id);
      }}
      className={`bg-panel pointer-events-auto max-w-md border px-4 py-2.5 text-left font-mono backdrop-blur transition-colors ${
        toast.tone === 'error'
          ? 'text-fg border-[color:var(--color-ramp-danger)]'
          : 'border-border text-fg-muted hover:text-fg'
      }`}
      style={{ fontSize: 'var(--text-meta)' }}
    >
      {toast.message}
    </button>
  );
}
